import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from './useToast';

export interface UseAsyncListOptions<T> {
  // mount 时是否立即拉取,默认 true;false 时等手动 reload
  immediate?: boolean;
  // 初始数据(数组场景传 [],对象场景传初值)
  initialData?: T;
  // 依赖项变化时自动重新拉取(默认 [],只在 mount 拉一次)
  deps?: ReadonlyArray<unknown>;
  // 失败提示文案,默认「加载失败」;传 onError 时此项被忽略
  errorMessage?: string;
  // 自定义错误处理;传了就不走默认 toast
  onError?: (e: unknown) => void;
  // 每次拉取成功后回调(如同步表单初值);在 data 更新之后触发
  onSuccess?: (data: T) => void;
}

export interface AsyncListState<T> {
  data: T;
  loading: boolean;
  error: unknown;
  reload: () => Promise<void>;
  setData: React.Dispatch<React.SetStateAction<T>>;
}

// useAsyncList 收口全站「拉一次列表/详情」的样板:loading 状态 + 调用时机 +
// try/catch + 失败 toast + 防竞态(只采纳最新一次请求结果)。fetcher 返回已解信封的
// 最终数据(调用方负责取 r.data),hook 不假设响应结构;信封 success=false 时在 fetcher 里
// throw 即可触发失败分支。
//
//   const { data, loading, reload } = useAsyncList(() => listX().then(r => r.data ?? []), { initialData: [] })
export function useAsyncList<T>(
  fetcher: () => Promise<T>,
  options: UseAsyncListOptions<T> = {},
): AsyncListState<T> {
  const {
    immediate = true,
    initialData,
    deps = [],
    errorMessage,
    onError,
    onSuccess,
  } = options;
  const toast = useToast();

  const [data, setData] = useState<T>(initialData as T);
  const [loading, setLoading] = useState<boolean>(immediate);
  const [error, setError] = useState<unknown>(undefined);

  // 全部走 ref,reload 身份保持稳定,不因渲染或 options 变化重建
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const errorMessageRef = useRef(errorMessage);
  errorMessageRef.current = errorMessage;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const reqIdRef = useRef(0);

  const reload = useCallback(async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    setError(undefined);
    try {
      const d = await fetcherRef.current();
      if (myId === reqIdRef.current) {
        setData(d);
        onSuccessRef.current?.(d);
      }
    } catch (e) {
      if (myId === reqIdRef.current) {
        setError(e);
        if (onErrorRef.current) onErrorRef.current(e);
        else toast.error(errorMessageRef.current ?? '加载失败');
      }
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (immediate) void reload();
  }, deps);

  return { data, loading, error, reload, setData };
}
